import { getVoiceAllowance, formatResetDay, VoiceAccess } from '../../services/voiceAccessService';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  useToast,
  Spinner,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { Mic, Clock, Zap, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import voicePassService, { VoicePass, VOICE_PASS_PRICES } from '../../services/voicePassService';
import walletService from '../../services/walletService';
import { isIOSVoiceVideoBlocked } from '../../utils/iosUtils';

interface VoicePassModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VoicePassModal: React.FC<VoicePassModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { currentUser, refreshProfile } = useAuth();
  const toast = useToast();
  
  const [loading, setLoading] = useState(false);
  const [allowance, setAllowance] = useState<VoiceAccess | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    getVoiceAllowance().then(setAllowance).catch(() => setAllowance(null));
  }, [isOpen]);
  const [purchasing, setPurchasing] = useState<'unlimited' | 'regular' | null>(null);
  const [currentPass, setCurrentPass] = useState<VoicePass | null>(null);
  const [remainingTime, setRemainingTime] = useState<string>('');
  const [balance, setBalance] = useState(0);

  // Load current pass status and balance
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser?.uid || !isOpen) return;
      
      setLoading(true);
      try {
        // Get current pass
        const passResult = await voicePassService.getUserVoicePass(currentUser.uid);
        if (passResult.valid && passResult.pass) {
          setCurrentPass(passResult.pass);
          setRemainingTime(passResult.remainingTime || '');
        } else {
          setCurrentPass(null);
          setRemainingTime('');
        }

        // Get balance
        const wallet = await walletService.getWallet(currentUser.uid);
        setBalance(wallet?.pkCash || 0);
      } catch (error) {
        console.error('Error loading voice pass data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser?.uid, isOpen]);

  // Update remaining time every minute
  useEffect(() => {
    if (!currentPass) return;

    const interval = setInterval(() => {
      const time = voicePassService.getTimeRemaining(currentPass.expiresAt);
      if (time === 'Expired') {
        setCurrentPass(null);
        setRemainingTime('');
      } else {
        setRemainingTime(time);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [currentPass]);

  const handlePurchase = async () => {
    if (!currentUser?.uid) return;

    setPurchasing('unlimited');
    try {
      const result = await voicePassService.purchaseUnlimitedPass(currentUser.uid);

      if (result.success && result.pass) {
        setCurrentPass(result.pass);
        setRemainingTime('24h 0m');
        toast({
          title: 'Voice Pass Activated!',

          description: 'Valid for 24 hours of unlimited voice tables',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Refresh balance
        const wallet = await walletService.getWallet(currentUser.uid);
        setBalance(wallet?.pkCash || 0);
        await refreshProfile();
      } else {
        toast({
          title: 'Purchase Failed',
          description: result.error || 'Could not complete purchase',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setPurchasing(null);
    }
  };

  const canAffordUnlimited = balance >= VOICE_PASS_PRICES.unlimited;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0} borderRadius={0}>
        {/* Header */}
        <HStack 
          justify="space-between" 
          px={4} 
          py={4}
          borderBottom="1px solid"
          borderColor="gray.800"
        >
          <Box w="60px" />
          <HStack spacing={2}>
            <Mic size={20} color="#00D9D9" />
            <Text color="white" fontSize="md" fontWeight="semibold">
              Voice Chat Pass
            </Text>
          </HStack>
          <Text
            color="gray.400"
            fontSize="md"
            cursor="pointer"
            onClick={onClose}
            _hover={{ color: 'white' }}
            w="60px"
            textAlign="right"
          >
            Done
          </Text>
        </HStack>

        <ModalBody py={6} px={4} overflowY="auto">
          {allowance && (
            <Box mb={4} p={3} bg="rgba(0,217,217,0.08)" border="1px solid" borderColor="rgba(0,217,217,0.3)" borderRadius="lg">
              <Text color="white" fontWeight="semibold" fontSize="sm">
                {allowance.remainingSeconds > 0
                  ? `${Math.ceil(allowance.remainingSeconds / 60)} of ${Math.round(allowance.weeklySeconds / 60)} free minutes left this week`
                  : 'Free minutes used up this week'}
              </Text>
              <Text color="gray.400" fontSize="xs">Free minutes reset {formatResetDay(allowance.resetsAtMs)}. A pass makes voice and video unlimited for 24 hours.</Text>
            </Box>
          )}
          <VStack spacing={6} align="stretch" maxW="500px" mx="auto">
            {/*  iOS: Voice chat not available yet */}

            {isIOSVoiceVideoBlocked() ? (
              <VStack spacing={4} py={10}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="8px"
                  p="12px 16px"
                  bg="rgba(245, 158, 11, 0.1)"
                  border="1px solid rgba(245, 158, 11, 0.3)"
                  borderRadius="8px"
                  w="100%"
                >
                  <Text fontSize="sm" color="#fbbf24">
                    Voice chat is not available in the iOS app. Open PokerSweepStars in Safari or Chrome to use voice/video features.
                  </Text>
                </Box>
              </VStack>
            ) : loading ? (
              <VStack py={10}>
                <Spinner color="cyan.400" size="lg" />
                <Text color="gray.400">Loading...</Text>
              </VStack>
            ) : (
              <>
                {/* Current Balance */}
                <Box 
                  bg="linear-gradient(135deg, #1a1a2e 0%, #0d1421 100%)"
                  p={4}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.700"
                >
                  <HStack justify="space-between">
                    <Text color="gray.400" fontSize="sm">Your PSS Coins Balance</Text>
                    <Text color="yellow.400" fontSize="lg" fontWeight="bold">
                      {balance.toFixed(2)}
                    </Text>
                  </HStack>
                </Box>

                {/* Active Pass Display */}
                {currentPass && currentPass.type === 'unlimited' && (
                  <Box 
                    bg="linear-gradient(135deg, rgba(0,217,217,0.15) 0%, rgba(0,100,100,0.15) 100%)"
                    p={5}
                    borderRadius="xl"
                    border="2px solid"
                    borderColor="cyan.500"
                  >
                    <VStack spacing={4}>
                      <HStack>
                        <Shield size={24} color="#00D9D9" />
                        <Text color="cyan.300" fontSize="lg" fontWeight="bold">
                          Active Voice Pass
                        </Text>
                        <Badge colorScheme="green" fontSize="xs">UNLIMITED</Badge>
                      </HStack>

                      {/* Time Remaining */}
                      <Box 
                        bg="blackAlpha.500" 
                        p={4} 
                        borderRadius="lg" 
                        w="100%"
                        textAlign="center"
                      >
                        <HStack justify="center" spacing={2}>
                          <Clock size={20} color="#00D9D9" />
                          <Text color="cyan.300" fontSize="xl" fontWeight="bold">
                            {remainingTime}
                          </Text>
                        </HStack>
                        <Text color="gray.400" fontSize="xs" mt={2}>remaining</Text>
                      </Box>

                      {/* Usage Count */}
                      <Text color="gray.400" fontSize="xs">
                        Used {currentPass.usageCount} time{currentPass.usageCount !== 1 ? 's' : ''}
                      </Text>
                    </VStack>
                  </Box>
                )}

                {/* Pass Options */}
                {!currentPass && (
                  <>
                    {/* Info Banner */}
                    <Box 
                      bg="cyan.900" 
                      p={4} 
                      borderRadius="xl"
                      borderLeft="4px solid"
                      borderColor="cyan.400"
                    >
                      <Text color="cyan.200" fontSize="sm">
                         Voice Chat Passes let you create voice-enabled tables without paying the per-table fee. 

                        Get an Unlimited Pass for 24 hours of unlimited table creation!
                      </Text>
                    </Box>

                    {/* Unlimited Pass Option */}
                    <Box
                      bg="linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(59,130,246,0.1) 100%)"
                      p={5}
                      borderRadius="xl"
                      border="2px solid"
                      borderColor="purple.500"
                      position="relative"
                      overflow="hidden"
                    >
                      {/* Best Value Badge */}
                      <Badge
                        position="absolute"
                        top={3}
                        right={3}
                        colorScheme="purple"
                        fontSize="xs"
                        px={2}
                      >
                        ⭐ BEST VALUE
                      </Badge>

                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <Zap size={24} color="#a855f7" />
                          <Text color="purple.300" fontSize="lg" fontWeight="bold">
                            Unlimited Pass
                          </Text>
                        </HStack>

                        <VStack align="start" spacing={2}>
                          <HStack>
                            <Text color="green.400"></Text>

                            <Text color="gray.300" fontSize="sm">24 hours of unlimited voice tables</Text>
                          </HStack>
                          <HStack>
                            <Text color="green.400"></Text>

                            <Text color="gray.300" fontSize="sm">Create as many tables as you want</Text>
                          </HStack>
                          <HStack>
                            <Text color="green.400"></Text>

                            <Text color="gray.300" fontSize="sm">No per-table fees</Text>
                          </HStack>
                        </VStack>

                        <Divider borderColor="purple.700" />

                        <HStack justify="space-between" align="center">
                          <VStack align="start" spacing={0}>
                            <Text color="white" fontSize="2xl" fontWeight="bold">
                              {VOICE_PASS_PRICES.unlimited.toFixed(2)}
                            </Text>
                            <Text color="gray.400" fontSize="xs">PSS Coins</Text>
                          </VStack>

                          <Box
                            as="button"
                            bg={canAffordUnlimited ? 'purple.500' : 'gray.600'}
                            color="white"
                            px={6}
                            py={3}
                            borderRadius="xl"
                            fontWeight="bold"
                            cursor={canAffordUnlimited ? 'pointer' : 'not-allowed'}
                            opacity={canAffordUnlimited ? 1 : 0.5}
                            onClick={() => canAffordUnlimited && handlePurchase()}
                            _hover={canAffordUnlimited ? { bg: 'purple.400' } : {}}
                            transition="all 0.2s"
                          >
                            {purchasing === 'unlimited' ? (
                              <Spinner size="sm" />
                            ) : (
                              'Get Pass'
                            )}
                          </Box>
                        </HStack>

                        {!canAffordUnlimited && (
                          <Text color="red.400" fontSize="xs" textAlign="center">
                            Insufficient balance (need {VOICE_PASS_PRICES.unlimited.toFixed(2)} PSS Coins)
                          </Text>
                        )}
                      </VStack>
                    </Box>

                    {/* Note about single table fee */}
                    <Box 
                      bg="#1C1C1E" 
                      p={4} 
                      borderRadius="xl"
                      borderLeft="3px solid"
                      borderColor="gray.600"
                    >
                      <Text color="gray.400" fontSize="sm">
                         <Text as="span" color="gray.300">Don't need unlimited?</Text> You can pay the one-time voice fee ({VOICE_PASS_PRICES.regular.toFixed(2)} PSS) when creating a table.

                      </Text>
                    </Box>
                  </>
                )}

                {/* Already have pass - show upgrade info */}
                {currentPass && (
                  <Box 
                    bg="#1C1C1E" 
                    p={4} 
                    borderRadius="xl"
                    textAlign="center"
                  >
                    <Text color="gray.400" fontSize="sm">
                       Your pass is active! Create voice-enabled tables without additional fees.

                    </Text>
                  </Box>
                )}

                {/* Note */}
                <Box 
                  bg="#1C1C1E" 
                  p={4} 
                  borderRadius="xl"
                >
                  <Text color="gray.500" fontSize="xs" textAlign="center">
                    Voice passes waive the voice chat fee when creating tables. 
                    Standard table rake still applies. Passes cannot be refunded.
                  </Text>
                </Box>

                {/* Spacer for safe area */}
                <Box h="80px" />
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VoicePassModal;